import { canonicalizeDateTime, parseDbTimestamp } from "@repo/shared";
import { addYears, format, isAfter, isValid, parseISO } from "date-fns";

/**
 * Format an ISO timestamp as a `yyyy-MM-dd` string in the user's local time.
 * Suitable for `<input type="date">` values.
 */
export function formatLocalDateInput(iso: string): string {
  const d = parseDbTimestamp(iso);
  return d && isValid(d) ? format(d, "yyyy-MM-dd") : "";
}

/**
 * QL-313 — a forecast grid date ("YYYY-MM-DD", always a month end) as a short
 * human date in the flow's language: "30 Sept 2027" / "30. sep. 2027". Read
 * as UTC so the day never shifts with the browser's zone.
 */
export function formatForecastGridDate(date: string, language: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  if (!isValid(d)) return date;
  return d.toLocaleDateString(language === "nb_NO" ? "nb-NO" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Format an ISO timestamp as `HH:mm` in the user's local time.
 * Suitable for `<input type="time">` values.
 */
export function formatLocalTimeInput(iso: string): string {
  const d = parseDbTimestamp(iso);
  return d && isValid(d) ? format(d, "HH:mm") : "";
}

/**
 * Combine a `yyyy-MM-dd` date and `HH:mm` time (both in the user's local time)
 * into a UTC ISO string, e.g. for storage in a `timestamp` DB column.
 */
export function localDateTimeToIso(date: string, time: string): string {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  if (!y || !m || !d || hh === undefined || mm === undefined) {
    return new Date().toISOString();
  }
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
}

/**
 * QL-211 — combine a `yyyy-MM-dd` date and an `HH:mm` time (both in the user's
 * local time) into the stored form of a datetime field value, UTC
 * `YYYY-MM-DDTHH:mm:ssZ`. Null when either part is incomplete — unlike
 * `localDateTimeToIso`, it never falls back to "now".
 */
export function localDateTimeToUtcField(date: string, time: string): string | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) return null;
  const month = Number(dateMatch[2]) - 1;
  const day = Number(dateMatch[3]);
  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  if (hours > 23 || minutes > 59) return null;
  const local = new Date(Number(dateMatch[1]), month, day, hours, minutes);
  // Date normalizes what does not exist (2026-02-31 → Mar 3, a time in the
  // spring-forward gap → an hour later); refuse those instead of shifting.
  if (
    !isValid(local) ||
    local.getMonth() !== month ||
    local.getDate() !== day ||
    local.getHours() !== hours ||
    local.getMinutes() !== minutes
  ) {
    return null;
  }
  return `${local.toISOString().slice(0, 19)}Z`;
}

/**
 * QL-211 — a stored datetime field value for display, in the viewer's local
 * time via the regional `formatDateTime`. A value that is not a canonical
 * datetime (e.g. a date left over from before the field's type changed) is
 * returned as stored.
 */
export function formatDateTimeFieldValue(
  value: string,
  formatDateTime: (value: string) => string,
): string {
  const canonical = canonicalizeDateTime(value);
  return canonical ? formatDateTime(canonical) : value;
}

/** Today's date in the user's local TZ as `yyyy-MM-dd`. */
export function todayLocalDateInput(): string {
  return format(new Date(), "yyyy-MM-dd");
}

/** Current local time as `HH:mm`. */
export function nowLocalTimeInput(): string {
  return format(new Date(), "HH:mm");
}

/**
 * Checks if a date string is within the last N years from now.
 * Returns false for invalid date strings.
 */
export function isWithinLastNYears(dateString: string, years: number): boolean {
  const date = parseISO(dateString);
  if (!isValid(date)) {
    return false;
  }
  const cutoffDate = addYears(new Date(), -years);
  return isAfter(date, cutoffDate);
}
